import {
  type CancelMaintenanceWorkOrderRequest,
  type CreateMaintenanceWorkOrderRequest,
  type MaintenanceWorkOrderListQuery,
  maintenanceWorkOrderDetailSchema,
  maintenanceWorkOrderListResponseSchema,
  type UpdateMaintenanceWorkOrderRequest,
} from '@gb-ticket/shared-types';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

const maintenanceTechniciansSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      displayName: z.string(),
      email: z.string(),
    }),
  ),
});

export const maintenanceWorkOrdersMineKey = (query: MaintenanceWorkOrderListQuery) =>
  ['maintenance', 'work-orders', 'mine', query] as const;

export const maintenanceWorkOrdersKey = (query: MaintenanceWorkOrderListQuery) =>
  ['maintenance', 'work-orders', query] as const;

export const maintenanceWorkOrderKey = (id: string) => ['maintenance', 'work-order', id] as const;

function workOrderQueryString(query: MaintenanceWorkOrderListQuery): string {
  const search = new URLSearchParams();
  search.set('page', String(query.page));
  search.set('pageSize', String(query.pageSize));
  search.set('sortBy', query.sortBy);
  search.set('sortOrder', query.sortOrder);
  if (query.status) {
    search.set('status', query.status);
  }
  if (query.assignedToId) {
    search.set('assignedToId', query.assignedToId);
  }
  if (query.customerId) {
    search.set('customerId', query.customerId);
  }
  if (query.projectId) {
    search.set('projectId', query.projectId);
  }
  if (query.installationId) {
    search.set('installationId', query.installationId);
  }
  if (query.templateName) {
    search.set('templateName', query.templateName);
  }
  if (query.completedFrom) {
    search.set('completedFrom', query.completedFrom);
  }
  if (query.completedTo) {
    search.set('completedTo', query.completedTo);
  }
  if (query.scheduledFrom) {
    search.set('scheduledFrom', query.scheduledFrom);
  }
  if (query.scheduledTo) {
    search.set('scheduledTo', query.scheduledTo);
  }
  if (query.includeArchived) {
    search.set('includeArchived', 'true');
  }
  return search.toString();
}

export function useMyMaintenanceWorkOrders(query: MaintenanceWorkOrderListQuery) {
  return useQuery({
    queryKey: maintenanceWorkOrdersMineKey(query),
    queryFn: () =>
      apiFetch(
        `/maintenance/work-orders/mine?${workOrderQueryString(query)}`,
        maintenanceWorkOrderListResponseSchema,
      ),
  });
}

export function useTrashedMaintenanceWorkOrders(query: MaintenanceWorkOrderListQuery) {
  return useQuery({
    queryKey: ['maintenance', 'work-orders', 'trash', query] as const,
    queryFn: () =>
      apiFetch(
        `/maintenance/work-orders/trash?${workOrderQueryString(query)}`,
        maintenanceWorkOrderListResponseSchema,
      ),
  });
}

export function useMaintenanceWorkOrders(query: MaintenanceWorkOrderListQuery) {
  return useQuery({
    queryKey: maintenanceWorkOrdersKey(query),
    queryFn: () =>
      apiFetch(
        `/maintenance/work-orders?${workOrderQueryString(query)}`,
        maintenanceWorkOrderListResponseSchema,
      ),
  });
}

export function useMaintenanceWorkOrder(id: string) {
  return useQuery({
    queryKey: maintenanceWorkOrderKey(id),
    queryFn: () => apiFetch(`/maintenance/work-orders/${id}`, maintenanceWorkOrderDetailSchema),
    enabled: Boolean(id),
  });
}

export function useMaintenanceTechnicians() {
  return useQuery({
    queryKey: ['maintenance', 'technicians'],
    queryFn: () => apiFetch('/maintenance/technicians', maintenanceTechniciansSchema),
  });
}

export function useCreateMaintenanceWorkOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMaintenanceWorkOrderRequest) =>
      apiFetch('/maintenance/work-orders', maintenanceWorkOrderDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useStartMaintenanceWorkOrder(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/maintenance/work-orders/${id}/start`, maintenanceWorkOrderDetailSchema, {
        method: 'POST',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: maintenanceWorkOrderKey(id) });
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useUpdateMaintenanceWorkOrder(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaintenanceWorkOrderRequest) =>
      apiFetch(`/maintenance/work-orders/${id}`, maintenanceWorkOrderDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: maintenanceWorkOrderKey(id) });
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useArchiveMaintenanceWorkOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/maintenance/work-orders/${id}/archive`, maintenanceWorkOrderDetailSchema, {
        method: 'POST',
      }),
    onSuccess: (order) => {
      client.setQueryData(maintenanceWorkOrderKey(order.id), order);
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useRestoreMaintenanceWorkOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/maintenance/work-orders/${id}/restore`, maintenanceWorkOrderDetailSchema, {
        method: 'POST',
      }),
    onSuccess: (order) => {
      client.setQueryData(maintenanceWorkOrderKey(order.id), order);
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useTrashMaintenanceWorkOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/maintenance/work-orders/${id}/trash`, maintenanceWorkOrderDetailSchema, {
        method: 'POST',
      }),
    onSuccess: (order) => {
      client.setQueryData(maintenanceWorkOrderKey(order.id), order);
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useRestoreTrashedMaintenanceWorkOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(
        `/maintenance/work-orders/${id}/restore-from-trash`,
        maintenanceWorkOrderDetailSchema,
        { method: 'POST' },
      ),
    onSuccess: (order) => {
      client.setQueryData(maintenanceWorkOrderKey(order.id), order);
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function usePurgeMaintenanceWorkOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/maintenance/work-orders/${id}/purge`, z.object({ deleted: z.literal(true) }), {
        method: 'POST',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useDeleteMaintenanceWorkOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/maintenance/work-orders/${id}`, z.object({ deleted: z.literal(true) }), {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}

export function useCancelMaintenanceWorkOrder(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CancelMaintenanceWorkOrderRequest) =>
      apiFetch(`/maintenance/work-orders/${id}/cancel`, maintenanceWorkOrderDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: maintenanceWorkOrderKey(id) });
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-orders'] });
    },
  });
}
